import { Button, Drawer, Link } from "@heroui/react";
import { useState } from "react";

import { AppLogo } from "@/src/components/AppLogo";
import { MenuIcon } from "@/src/components/icons";
import { AccountCard } from "@/src/components/layout/AccountCard";
import { AppBreadcrumb } from "@/src/components/layout/AppBreadcrumb";
import { SidebarNav } from "@/src/components/layout/SidebarNav";
import { LocaleToggle } from "@/src/components/LocaleToggle";
import { ThemeToggle } from "@/src/components/ThemeToggle";
import { useTranslations } from "@/src/i18n/i18n";
import { ROUTES } from "@/src/routes/paths";

/**
 * En-tête de l'administration.
 *
 * Porte trois choses : **où l'on est** (le fil d'Ariane), **comment aller
 * ailleurs** sur petit écran (le tiroir), et les **réglages d'affichage**
 * (langue, thème).
 *
 * ⚠️ Le fil n'est pas passé par les écrans : sa section se déduit de l'URL via
 * `NAVIGATION`. Un écran n'a rien à faire pour y figurer, et le fil ne peut pas
 * diverger du libellé du lien qui y mène. Seul un maillon **final**
 * (« nolan.dice ») vient de l'écran, seul à pouvoir le nommer.
 */
export function AppHeader() {
  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-separator bg-surface px-4 sm:px-6">
      <MobileNav />

      <AppBreadcrumb />

      {/* `ml-auto` plutôt qu'un `justify-between` : le fil reste collé à
          gauche même quand les réglages changent de largeur (« EN » → « FR »). */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <LocaleToggle />
        <ThemeToggle />
      </div>
    </header>
  );
}

/**
 * La même navigation, en tiroir, sous `lg`.
 *
 * L'état est contrôlé pour une seule raison : **refermer le tiroir après un
 * clic sur un lien**. Sans ça, la navigation a lieu derrière un panneau resté
 * ouvert, et l'écran d'arrivée est masqué.
 */
function MobileNav() {
  const [isOpen, setOpen] = useState(false);
  const ta = useTranslations("a11y");
  const tg = useTranslations("global");

  return (
    <Drawer isOpen={isOpen} onOpenChange={setOpen}>
      <Button
        aria-label={ta("open-navigation")}
        className="lg:hidden"
        isIconOnly
        size="sm"
        variant="ghost"
      >
        <MenuIcon className="size-5" />
      </Button>

      <Drawer.Backdrop>
        <Drawer.Content placement="left">
          <Drawer.Dialog aria-label={ta("main-navigation")}>
            <Drawer.Header className="flex-row items-center justify-between gap-2">
              {/* La marque ramène à la racine, comme dans la barre latérale —
                  et referme le tiroir derrière elle, sinon le tableau de bord
                  s'affiche sous un panneau resté ouvert. */}
              <Link
                className="flex min-w-0 items-center gap-2 hover:no-underline"
                href={ROUTES.dashboard}
                onPress={() => setOpen(false)}
              >
                <AppLogo className="size-8 rounded-lg" />
                <Drawer.Heading className="font-semibold tracking-tight">
                  {tg("app-name")}
                </Drawer.Heading>
              </Link>

              {/*
                Fermer se fait déjà par Échap et par le fond, mais aucun des
                deux ne se voit au doigt : le bouton est la seule sortie
                visible sur un écran tactile.

                `aria-label` explicite : le `CloseButton` de HeroUI annonce
                « Close » en anglais en dur, quelle que soit la langue.
              */}
              <Drawer.CloseTrigger aria-label={ta("close-navigation")} />
            </Drawer.Header>

            <Drawer.Body>
              <SidebarNav onNavigate={() => setOpen(false)} />
            </Drawer.Body>

            {/* `items-stretch` : le pied de tiroir centre ses enfants par
                défaut, ce qui décalerait le bloc compte par rapport aux liens
                juste au-dessus. */}
            <Drawer.Footer className="flex-col items-stretch">
              <AccountCard />
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
